import type { DatePickerTriggerSlotProps } from './date-picker-trigger/DatePickerTrigger.types'
import { offset } from '@floating-ui/react'
import cn from 'classnames'

import { useEffect, useRef, useState } from 'react'
import ReactDatePicker, { registerLocale } from 'react-datepicker'

import { Typography } from '../typography'
import { DatePickerHeader } from './date-picker-header/DatePickerHeader'
import { DatePickerTrigger } from './date-picker-trigger/DatePickerTrigger'
import styles from './DatePicker.module.css'
import { countryCodes, DatePickerCalendarProps } from './DatePickerCalendar.types'
import { focusPreSelectedDay } from './focusPreSelectedDay'
import { getDatePickerOpenToDate } from './getDatePickerOpenToDate'

// react-datepicker v7 always applies offset(10) and Floating UI sums offsets; v4 placed the popover flush with the trigger wrapper.
const REACT_DATEPICKER_DEFAULT_OFFSET = 10

function getTriggerSlotProps({
  className,
  placeholder,
  label,
  helpText,
  status,
  feedbackText,
  isOpen,
  calendarAriaLabel,
  disabled,
}: Pick<
  DatePickerCalendarProps,
  'className' | 'placeholder' | 'label' | 'helpText' | 'status' | 'feedbackText' | 'isOpen' | 'calendarAriaLabel' | 'disabled'
>): DatePickerTriggerSlotProps {
  return {
    className,
    placeholder,
    label,
    helpText,
    status,
    feedbackText,
    isOpen,
    calendarAriaLabel,
    disabled,
  }
}

export function DatePickerCalendar({
  className,
  label,
  helpText,
  status = 'default',
  feedbackText,
  placeholder,
  disabled,
  isOpen,
  minDate,
  maxDate,
  excludeDates,
  includeDates,
  calendarAriaLabel,
  locale = 'en-GB',
  dateFormat,
  nextMonthAriaLabel,
  previousMonthAriaLabel,
  shouldCloseOnSelect,
  selectsRange,
  selected,
  startDate,
  endDate,
  onChange,
  onSelect,
  onOpen,
  onClose,
  onKeyDown,
  showWeekNumbers,
  weekLabel = 'Week',
  monthsShown = 1,
  inline = false,
  renderTriggerSlot,
  highlightRange = false,
}: DatePickerCalendarProps) {
  registerLocale(locale, countryCodes[locale])
  const triggerSlotProps = inline
    ? undefined
    : getTriggerSlotProps({
        className,
        placeholder,
        label,
        helpText,
        status,
        feedbackText,
        isOpen,
        calendarAriaLabel,
        disabled,
      })
  const [calendarKey, setCalendarKey] = useState(0)
  const previousIsOpenRef = useRef(isOpen)
  const shouldResetCalendarView = monthsShown > 1
  const openToDate = getDatePickerOpenToDate({
    selectsRange,
    selected,
    startDate,
    monthsShown,
  })
  const defaultTriggerInput = calendarAriaLabel === undefined
    ? undefined
    : (
        <DatePickerTrigger
          {...triggerSlotProps}
          calendarAriaLabel={calendarAriaLabel}
        />
      )

  const customInput = !inline && triggerSlotProps
    ? renderTriggerSlot?.(triggerSlotProps) ?? defaultTriggerInput
    : undefined

  useEffect(() => {
    if (shouldResetCalendarView && isOpen && !previousIsOpenRef.current) {
      setCalendarKey(previousKey => previousKey + 1)
    }

    previousIsOpenRef.current = isOpen
  }, [isOpen, shouldResetCalendarView])

  return (
    <div
      className={cn(styles.wrapper, className, {
        [styles.wrapperInline]: inline,
        [styles.wrapperWithWeekNumbers]: Boolean(showWeekNumbers),
        [styles.wrapperWithMultipleMonths]: monthsShown > 1,
        [styles.wrapperHighlightRange]: highlightRange,
      })}
    >
      <ReactDatePicker
        key={shouldResetCalendarView ? calendarKey : undefined}
        inline={inline || undefined}
        open={inline ? undefined : isOpen}
        disabled={disabled}
        monthsShown={monthsShown}
        focusSelectedMonth={false}
        openToDate={openToDate}
        showWeekNumbers={Boolean(showWeekNumbers)}
        weekLabel={weekLabel}
        placeholderText={placeholder}
        selected={selected}
        {...(selectsRange ? { selectsRange: true, onChange } : { onChange })}
        startDate={startDate}
        endDate={endDate}
        minDate={minDate ?? undefined}
        maxDate={maxDate ?? undefined}
        excludeDates={excludeDates}
        includeDates={includeDates}
        dateFormat={dateFormat}
        onInputClick={inline ? undefined : onOpen}
        onSelect={onSelect}
        onClickOutside={onClose}
        onKeyDown={(event) => {
          onKeyDown(event)
          focusPreSelectedDay(event)
        }}
        renderDayContents={dayOfMonth => (
          <>
            <span className={styles.dayRangeBand} aria-hidden="true" />
            <Typography className={styles.dayLabel} variant="body-s-bold-snug">
              {dayOfMonth}
            </Typography>
          </>
        )}
        renderCustomHeader={headerProps => (
          <DatePickerHeader
            {...headerProps}
            localeString={locale}
            monthsShown={monthsShown}
            nextMonthAriaLabel={nextMonthAriaLabel}
            previousMonthAriaLabel={previousMonthAriaLabel}
          />
        )}
        shouldCloseOnSelect={shouldCloseOnSelect}
        popperPlacement="bottom-start"
        popperModifiers={[offset(-REACT_DATEPICKER_DEFAULT_OFFSET)]}
        customInput={customInput}
        locale={locale}
      />
    </div>
  )
}
